import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Message, Modal } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Mails, Contact, ShieldCheck, CircleCheckBig, ChevronRight, EyeOff } from 'lucide-react';

import { ExclusionsField, parseExclusionsInput } from './ExclusionsField';

const ICON_BOX_BASE_SX = {
  width: '32px',
  height: '32px',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
};

const iconBoxSx = (checked) => ({
  ...ICON_BOX_BASE_SX,
  bg: checked ? 'purple.100' : 'neutral.50',
  color: checked ? 'primary' : 'neutral.400',
});

const CHECKBOX_BOX_SX = {
  width: '32px',
  height: '32px',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
};

const ToggleRow = ({ id, icon: Icon, title, description, checked, onChange, body }) => (
  <Flex flexDirection="column" sx={{ width: '100%', gap: body ? '8px' : 0 }}>
    <Flex as="label" htmlFor={id} sx={{ width: '100%', gap: '16px', alignItems: 'flex-start', cursor: 'pointer' }}>
      <Flex as="span" sx={iconBoxSx(checked)} aria-hidden="true">
        <Box as={Icon} size="16px" />
      </Flex>
      <Flex flexDirection="column" sx={{ flex: 1, minWidth: 0, gap: '0px' }}>
        <Text color="text.dark" fontSize="14px" fontWeight="500" lineHeight="1.4">
          {title}
        </Text>
        <Text color="text.main" fontSize="14px" lineHeight="1.4" mt="4px">
          {description}
        </Text>
      </Flex>
      <Flex sx={CHECKBOX_BOX_SX}>
        <Checkbox
          id={id}
          name={id}
          label=""
          value={checked}
          checked={checked}
          onChange={() => onChange(!checked)}
          containerProps={{ as: 'div' }}
        />
      </Flex>
    </Flex>
    {body && <Box>{body}</Box>}
  </Flex>
);

ToggleRow.propTypes = {
  id: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  body: PropTypes.node,
};

ToggleRow.defaultProps = { body: null };

// Kept in lockstep with `backend/app/services/google/oauth_scope_builder.rb`.
// Drift here surfaces as a 422 because the backend re-validates granted scopes.
//
// Both user-facing toggles ultimately consume the same email + calendar
// interaction stream:
//   - Relationship strength enrichment computes per-(user,contact) stats from
//     it.
//   - Contacts sync auto-creates Getro contacts when an external counterpart
//     in that stream doesn't already match an existing contact.
// So `gmail.metadata` + `calendar.events.readonly` are requested whenever EITHER
// toggle is on. The legacy People-API scopes (`contacts.readonly` /
// `contacts.other.readonly`) are never requested for new connects — the
// auto-create path no longer reads the Google address book.
export const buildScopeString = ({ relationshipSyncEnabled, contactsSyncEnabled }) => {
  const needsInteractionStream = relationshipSyncEnabled || contactsSyncEnabled;
  const scopes = ['openid', 'email', 'profile'];
  if (needsInteractionStream) {
    scopes.push('https://www.googleapis.com/auth/gmail.metadata');
    scopes.push('https://www.googleapis.com/auth/calendar.events.readonly');
  }
  return scopes.join(' ');
};

const FooterNote = ({ icon: Icon, children }) => (
  <Flex sx={{ gap: '8px', alignItems: 'flex-start' }}>
    <Flex
      sx={{
        width: '16px',
        height: '16px',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        color: 'neutral.500',
      }}
    >
      <Box as={Icon} size="12px" />
    </Flex>
    <Text color="text.main" fontSize="12px" lineHeight="1.4">
      {children}
    </Text>
  </Flex>
);

FooterNote.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
};

const LEGACY_CONTACTS_SCOPES = [
  'https://www.googleapis.com/auth/contacts.readonly',
  'https://www.googleapis.com/auth/contacts.other.readonly',
];
const WALK_SCOPES = [
  'https://www.googleapis.com/auth/gmail.metadata',
  'https://www.googleapis.com/auth/calendar.events.readonly',
  // Pre-GET-615 grant; still counts as walk until the admin reconnects.
  'https://www.googleapis.com/auth/calendar.readonly',
];

// Seeds the modal state from the account already on record so that
// reconnecting preserves the admin's prior choices. First-time connect (null
// preferences) defaults to all-on. The contacts-sync seed mirrors the card's
// two-population gate (see GoogleIntegrationCard): walk-scope accounts have
// been through the modal at least once so `contactsSyncEnabled` is an
// explicit user choice; legacy contacts-only accounts only carry People-API
// scopes and round-trip the column at its `false` default even while still
// producing contacts, so the grant is the source of truth for them. Without
// this branch a reconnect would surface the toggle pre-toggled OFF and turn
// off an active sync on submit.
const seedFromPreferences = (initialPreferences) => {
  if (!initialPreferences) {
    return {
      relationshipSync: true,
      contactsSync: true,
      exclusionsRaw: '',
      excludedEmails: [],
      excludedDomains: [],
    };
  }
  const scopes = initialPreferences.scopes || [];
  const hasWalkScope = WALK_SCOPES.some((scope) => scopes.includes(scope));
  const hasLegacyContactsScope = LEGACY_CONTACTS_SCOPES.some((scope) => scopes.includes(scope));
  const contactsSync = hasWalkScope
    ? initialPreferences.contactsSyncEnabled !== false
    : hasLegacyContactsScope || initialPreferences.contactsSyncEnabled !== false;
  const excludedEmails = initialPreferences.excludedEmails || [];
  const excludedDomains = initialPreferences.excludedDomains || [];
  return {
    relationshipSync: initialPreferences.relationshipStrengthEnabled !== false,
    contactsSync,
    exclusionsRaw: [...excludedEmails, ...excludedDomains].join('\n'),
    excludedEmails,
    excludedDomains,
  };
};

export const GoogleConnectModal = ({ isOpen, isSubmitting, initialPreferences, onCancel, onSubmit }) => {
  const initialSeed = useMemo(() => seedFromPreferences(initialPreferences), [initialPreferences]);
  const [relationshipSync, setRelationshipSync] = useState(initialSeed.relationshipSync);
  const [contactsSync, setContactsSync] = useState(initialSeed.contactsSync);
  const [exclusionsRaw, setExclusionsRaw] = useState(initialSeed.exclusionsRaw);

  // Reseed only on the `isOpen` false→true transition. Reseeding on every
  // `initialPreferences` identity change would clobber in-progress toggle and
  // exclusion edits whenever the parent's `googleSync` refetch produces a new
  // object reference mid-modal. The latest preferences are still captured at
  // open time because the effect runs after the render that flipped `isOpen`.
  //
  // `seededExclusionsRef` snapshots the open-time exclusion arrays so the
  // submit fallback uses what the admin actually saw, not whatever the live
  // prop happens to be at submit time (a mid-modal refetch could otherwise
  // hand us a different account's exclusions).
  const wasOpenRef = useRef(false);
  const seededExclusionsRef = useRef({ excludedEmails: [], excludedDomains: [] });
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const seed = seedFromPreferences(initialPreferences);
      setRelationshipSync(seed.relationshipSync);
      setContactsSync(seed.contactsSync);
      setExclusionsRaw(seed.exclusionsRaw);
      seededExclusionsRef.current = {
        excludedEmails: seed.excludedEmails,
        excludedDomains: seed.excludedDomains,
      };
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialPreferences]);

  const atLeastOneEnabled = relationshipSync || contactsSync;

  const parsedExclusions = useMemo(() => parseExclusionsInput(exclusionsRaw), [exclusionsRaw]);

  // Exclusions only apply to the contacts-sync source: the `ExclusionsField`
  // is disabled when contacts sync is off, so any invalid text the user left
  // behind is unreachable. Gating submit on `parsedExclusions.invalid` only
  // when `contactsSync` is on prevents a stuck-state where the user can't
  // both clear the invalid input and submit.
  const exclusionsBlocksSubmit = contactsSync && parsedExclusions.invalid.length > 0;

  // When contacts sync is off the exclusion field is disabled, so its parsed
  // value can be stale. Fall back to the open-time snapshot on submit so a
  // reconnect that turns contacts sync off does not silently wipe the admin's
  // previously saved exclusions on the server, AND so a mid-modal refetch on
  // `initialPreferences` cannot redirect the submit to a different account's
  // exclusion arrays.
  const handleSubmit = useCallback(() => {
    if (isSubmitting) return;
    if (!atLeastOneEnabled) return;
    if (exclusionsBlocksSubmit) return;

    const { excludedEmails: seededExcludedEmails, excludedDomains: seededExcludedDomains } =
      seededExclusionsRef.current;

    onSubmit({
      relationshipSyncEnabled: relationshipSync,
      contactsSyncEnabled: contactsSync,
      excludedEmails: contactsSync ? parsedExclusions.emails : seededExcludedEmails,
      excludedDomains: contactsSync ? parsedExclusions.domains : seededExcludedDomains,
      scope: buildScopeString({
        relationshipSyncEnabled: relationshipSync,
        contactsSyncEnabled: contactsSync,
      }),
    });
  }, [
    isSubmitting,
    atLeastOneEnabled,
    exclusionsBlocksSubmit,
    parsedExclusions,
    relationshipSync,
    contactsSync,
    onSubmit,
  ]);

  if (!isOpen) return null;

  return (
    <Modal
      title="Connect your Google account"
      isOpen
      onCancel={onCancel}
      actions={
        <Flex justifyContent="flex-end" sx={{ gap: '8px' }}>
          <Button onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button
            data-testid="gwi-submit"
            variant="primary"
            loading={isSubmitting}
            disabled={!atLeastOneEnabled || exclusionsBlocksSubmit || isSubmitting}
            onClick={handleSubmit}
          >
            <Flex as="span" sx={{ alignItems: 'center', gap: '8px' }}>
              <span>Continue to Google</span>
              <Box as={ChevronRight} size="16px" aria-hidden="true" />
            </Flex>
          </Button>
        </Flex>
      }
    >
      <Flex flexDirection="column" sx={{ gap: '16px' }}>
        <Text color="text.main" fontSize="14px" lineHeight="1.4">
          Choose how Getro uses your Google activity. We recommend both for the best experience.
        </Text>

        <Flex flexDirection="column" sx={{ gap: '16px' }}>
          <ToggleRow
            id="gwi-toggle-relationship"
            icon={Mails}
            title="Reachability & Connections"
            description="Getro uses who you email or meet — and when — to rate your team's connections and calculate each contact's Reachability."
            checked={relationshipSync}
            onChange={setRelationshipSync}
          />
          <Box height="1px" bg="border.subtle" />
          <ToggleRow
            id="gwi-toggle-contacts"
            icon={Contact}
            title="Add people you email or meet"
            description="Getro adds people you interact with, based on email and calendar activity. Your Google Contacts address book is never accessed — we don't request that permission."
            checked={contactsSync}
            onChange={setContactsSync}
            body={<ExclusionsField value={exclusionsRaw} onChange={setExclusionsRaw} disabled={!contactsSync} />}
          />
        </Flex>

        {!atLeastOneEnabled && <Message type="error">Turn on at least one source to continue.</Message>}

        <Box bg="neutral.20" sx={{ borderRadius: '4px', p: '8px' }}>
          <Flex flexDirection="column" sx={{ gap: '8px' }}>
            <FooterNote icon={EyeOff}>
              Getro uses only who you interacted with and when. Our Gmail permission is metadata-only — message content
              can&apos;t be read.
            </FooterNote>
            <FooterNote icon={ShieldCheck}>Getro is secure, GDPR-compliant, and SOC II certified.</FooterNote>
            <FooterNote icon={CircleCheckBig}>
              Use of your Google data adheres to the{' '}
              <Box
                as="a"
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.main', textDecoration: 'underline' }}
              >
                Google API Services User Data Policy
              </Box>
              , including the Limited Use requirements.
            </FooterNote>
          </Flex>
        </Box>
      </Flex>
    </Modal>
  );
};

GoogleConnectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool,
  initialPreferences: PropTypes.shape({
    emailSyncEnabled: PropTypes.bool,
    calendarSyncEnabled: PropTypes.bool,
    contactsSyncEnabled: PropTypes.bool,
    relationshipStrengthEnabled: PropTypes.bool,
    excludedEmails: PropTypes.arrayOf(PropTypes.string),
    excludedDomains: PropTypes.arrayOf(PropTypes.string),
  }),
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

GoogleConnectModal.defaultProps = {
  isSubmitting: false,
  initialPreferences: null,
};
